import { useState, useContext } from 'react';
import { List, Alert } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { useMutation, useQuery } from '@tanstack/react-query';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import {
    FormValidated, FormContext, FlexColumnStart, toasty,
} from '../../common/forms/FormElements';
import { SaveButton } from '../../common/forms/SaveButton';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { BlockBorder } from '../../common/Block';

const loadExportOptions = debounce((inputValue, callback) => {
    httpClient.js(ApiRoutes.typeAheads.payrollExports(inputValue))
        .then((data) => callback(data));
}, 250);

const initialValues = {
    payrollExportId: null,
    dispatchLocationIds: [],
    employeeIds: [],
};

const PayrollExportField = () => {
    const { setFieldValue } = useContext(FormContext);

    return (
        <ValidatedSelect
            component={AsyncSelect}
            loadOptions={loadExportOptions}
            placeholder="Type Payroll Export Number Here"
            id="payrollExportId"
            name="payrollExportId"
            label="Payroll Export"
            asObject
            cacheOptions
            required
            error="Payroll Export is required."
            onChange={() => {
                setFieldValue('dispatchLocationIds', []);
                setFieldValue('employeeIds', []);
            }}
        />
    );
};

const OfficesField = () => {
    const { values, setFieldValue } = useContext(FormContext);
    const payrollExportId = values.payrollExportId?.value;

    const { data: options, isLoading, isError } = useQuery({
        queryKey: ['payrollExportOffices', payrollExportId],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.payrollExportOffices(payrollExportId)),
        enabled: !!payrollExportId,
    });

    return (
        <>
            <ValidatedSelect
                id="dispatchLocationIds"
                name="dispatchLocationIds"
                label="Offices"
                options={options ?? []}
                isLoading={isLoading}
                isDisabled={!payrollExportId}
                required
                error="At least one office selection is required."
                isMulti
                onChange={() => setFieldValue('employeeIds', [])}
            />

            {isError && (
                <Alert
                    color="danger"
                    className="mt-n2 mb-3"
                >
                    Failed to load offices for this export. Try reselecting the export.
                </Alert>
            )}
        </>
    );
};

const EmployeesField = () => {
    const { values } = useContext(FormContext);
    const payrollExportId = values.payrollExportId?.value;
    const officeIds = (values.dispatchLocationIds ?? []).map((o) => o.value);

    const { data: options, isLoading, isError } = useQuery({
        queryKey: ['payrollExportEmployees', payrollExportId, officeIds],
        queryFn: () => httpClient.js(
            ApiRoutes.typeAheads.payrollExportEmployees(payrollExportId, officeIds),
        ),
        enabled: !!payrollExportId && officeIds.length > 0,
    });

    return (
        <>
            <ValidatedSelect
                id="employeeIds"
                name="employeeIds"
                label="Employees"
                options={options ?? []}
                isLoading={isLoading}
                isDisabled={!officeIds.length}
                required
                error="At least one employee selection is required."
                isMulti
                formatOptionLabel={(opt, { context }) => {
                    if (context === 'menu') {
                        return (
                            <FlexColumnStart>
                                <span>{opt.label}</span>
                                {Boolean(opt.officeName) && (
                                    <small className="text-muted mt-1">{opt.officeName}</small>
                                )}
                            </FlexColumnStart>
                        );
                    }

                    return <span>{opt.label}</span>;
                }}
            />

            {isError && (
                <Alert
                    color="danger"
                    className="mt-n2 mb-3"
                >
                    Failed to load employees for this export/office selection. Try reselecting the office(s).
                </Alert>
            )}
        </>
    );
};

/**
 * Shared form for rolling back a payroll export for selected offices and employees.
 * Can be used by both Admin Tools and Support Tools pages.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */
const RollbackPayrollExportForm = ({ onSuccess }) => {
    const [isValidated, setIsValidated] = useState(false);
    const [formKey, setFormKey] = useState(0);

    const { mutateAsync } = useMutation({
        mutationFn: (payload) => httpClient.post(ApiRoutes.adminTool.rollbackPayrollExport(), payload),
    });

    const handleSubmit = async (values) => {
        await mutateAsync({
            payrollExportId: values.payrollExportId?.value,
            dispatchLocationIds: (values.dispatchLocationIds ?? []).map((o) => o.value),
            employeeIds: (values.employeeIds ?? []).map((o) => o.value),
        });

        toasty.success('Rollback Payroll Export Succeeded');
        setFormKey((key) => key + 1);

        if (onSuccess) {
            await onSuccess();
        }
    };

    return (
        <FormValidated
            key={formKey}
            id="rollbackPayrollExportForm"
            name="rollbackPayrollExportForm"
            initialValues={initialValues}
            isValidated={isValidated}
            setIsValidated={setIsValidated}
            onSubmit={handleSubmit}
        >
            {() => (
                <>
                    <PayrollExportField />
                    <OfficesField />
                    <EmployeesField />

                    <BlockBorder />

                    <div className="d-flex justify-content-center">
                        <SaveButton label="Submit" />
                    </div>
                </>
            )}
        </FormValidated>
    );
};

/**
 * Renders the execution history details for RollbackPayrollExport.
 * Can be used by both Admin and SupportTools ExecutionHistory components.
 */
export const RollbackPayrollExportHistoryDetails = ({ record }) => (
    <List type="unstyled">
        {record.input.payrollExportId && (
            <li>
                <strong>Payroll Export:</strong>
                {' '}
                {record.input.payrollExportId}
            </li>
        )}
        {record.output?.rolledBackPayrollIds && record.output.rolledBackPayrollIds.length > 0 && (
            <li>
                <strong>Payroll Records Rolled Back:</strong>
                {' '}
                {record.output.rolledBackPayrollIds.length}
            </li>
        )}
        {record.output?.message && (
            <li>
                <strong>Message:</strong>
                {' '}
                {record.output.message}
            </li>
        )}
    </List>
);

export { RollbackPayrollExportForm };
export default RollbackPayrollExportForm;
